/**
 * Book Meeting's save rules and create call, shared with the calendar's
 * compact booking pop-up.
 */
import { MeetingsService, SessionFrequency } from "client/openapi";
import moment from "moment-timezone";
import type { MeetingDetails, TutorOverride } from ".";
import { announceMeetingsChanged } from "util/meetingsChanged";

/**
 * Why these sessions cannot be saved yet, or "" when they can.
 * The server does not check these fields, so this must stay this strict.
 */
export function getMeetingDraftError(meetings: MeetingDetails[]): string {
  // Check for duplicate sessions
  const stringified_meetings = meetings.map((obj) => JSON.stringify(obj));
  if (new Set(stringified_meetings).size !== meetings.length) {
    return "Please remove duplicate sessions";
  }

  // Use a simple for loop so we can break out on first error
  for (let i = 0; i < meetings.length; i++) {
    const meeting = meetings[i];
    if (
      !meeting ||
      meeting.org_subject === undefined ||
      meeting.student_ids.length === 0 ||
      meeting.tutor_ids.length === 0 ||
      (!meeting.location && !meeting.other_location) ||
      !meeting.start ||
      !meeting.duration ||
      !meeting.frequency ||
      (meeting.frequency !== SessionFrequency.SINGLE &&
        !meeting.ends_on_or_before)
    ) {
      const missingFields: string[] = [];
      if (meeting.org_subject === undefined) missingFields.push("subject");
      if (meeting.student_ids.length === 0) missingFields.push("students");
      if (meeting.tutor_ids.length === 0) missingFields.push("tutors");
      if (!meeting.location && !meeting.other_location)
        missingFields.push("location");
      if (!meeting.start) missingFields.push("start time");
      if (!meeting.duration) missingFields.push("duration");
      if (!meeting.frequency) missingFields.push("frequency");
      if (
        meeting.frequency !== SessionFrequency.SINGLE &&
        !meeting.ends_on_or_before
      ) {
        missingFields.push("end date");
      }

      return `Please add the following: ${missingFields.join(", ")}`;
    }
  }

  // If we get here, all validations passed
  return "";
}

/**
 * The draft without the tutors' choice for busy weeks. Their weeks may be
 * busy for the room again, so a room chosen for busy weeks goes too.
 */
export const withoutTutorOverrides = (
  meeting: MeetingDetails
): MeetingDetails =>
  meeting.tutor_overrides
    ? { ...meeting, tutor_overrides: undefined, room_overrides: undefined }
    : meeting;

/**
 * The draft with the tutors' choice for some busy weeks, in place of any
 * earlier choice for them. The meeting keeps its own room on a moved week (it
 * was checked free then), so a room chosen for that week goes.
 */
export function withTutorChoices(
  meeting: MeetingDetails,
  choices: TutorOverride[]
): MeetingDetails {
  const dates = new Set(choices.map((choice) => choice.date));
  const rooms = meeting.room_overrides?.filter(
    (override) => !dates.has(override.date)
  );
  return {
    ...meeting,
    tutor_overrides: [
      ...(meeting.tutor_overrides ?? []).filter(
        (override) => !dates.has(override.date)
      ),
      ...choices,
    ],
    room_overrides: rooms?.length ? rooms : undefined,
  };
}

/** Creates each session with its own POST /meetings, as Book Meeting does. */
export function createMeetings(
  meetingsToCreate: MeetingDetails[],
  orgSubjectId: number
) {
  return Promise.all(
    meetingsToCreate.map((meetingInfo) =>
      MeetingsService.createMeeting({
        requestBody: {
          org_id: meetingInfo.org_id,
          start: moment.utc(meetingInfo.start).format(),
          duration: meetingInfo.duration,
          name:
            !meetingInfo.name || meetingInfo.name.trim() === ""
              ? (meetingInfo.org_subject?.name as string)
              : meetingInfo.name,
          add_students: meetingInfo.student_ids,
          add_tutors: meetingInfo.tutor_ids,
          org_subject_id: orgSubjectId,
          location_id: meetingInfo.location?.id,
          other_location: meetingInfo.other_location,
          frequency: meetingInfo.frequency,
          ends_on_or_before: meetingInfo.ends_on_or_before
            ? moment(meetingInfo.ends_on_or_before).endOf("day").utc().format()
            : undefined,
          // The tutors' skipped weeks are skipped as the room's are; their
          // moved weeks start at the new time.
          room_overrides: [
            ...(meetingInfo.room_overrides ?? []),
            ...(meetingInfo.tutor_overrides ?? [])
              .filter((override) => !override.start)
              .map((override) => ({ date: override.date, location_id: null })),
          ],
          start_overrides: (meetingInfo.tutor_overrides ?? []).flatMap(
            (override) =>
              override.start
                ? [
                    {
                      date: override.date,
                      start: moment.utc(override.start).format(),
                    },
                  ]
                : []
          ),
        },
      })
    )
  ).then((created) => {
    announceMeetingsChanged();
    return created;
  });
}
